import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { VehicleListItemDto } from "../models/transport/vehicle";
import { EmployeeListItemDto } from "../models/employee/employee";
import { SalaryRowTypeItem } from "../models/salary/salaryRowTypeItem";
import { WorkTimeTypeListItem } from "../models/workTimeType/workTimeTypeListItem";
import { ICostCenterItem } from "../models/costCenter/costCenterItem";
import { Base } from "../framework/base";
import { getVehicles } from "../services/vehicleService";
import { getEmployees } from "../services/employeeServiceNew";
import { getSalaryRowTypeItems } from "../services/salaryRowTypeService";
import { getCostCenterItems } from "../services/costCenterService";
import { getAllWorkTimeTypes } from "../services/workTimeTypeService";
import { ICustomerItem } from "../models/customer/customerItem";
import { getCustomerItems } from "../services/customerService";
import { FuelType } from "../models/vehicle/fuelType";
import { employeeGroupListItemDto } from "../models/employeeGroup/employeeGroup";
import { getEmployeeGroups } from "../services/employeeGroupService";

export interface Filters {
    timeRange: [string, string];
    selectedVehicles: string[];
    selectedCostCenters: string[];
    selectedEmployees: string[];
    selectedEmployeeGroups: string[];
    selectedCustomers: string[];
    selectedWorkTimeTypes: string[];
}

export interface FuelEmission
{
    fuelType : FuelType;
    emissionFactor: number;
}

export enum GroupingValue {
    Date = "dateStr",
    Employee = "employeeId",
    Vehicle = "vehicleId",
    Customer = "customerId",
    CostCenter = "costCenterId",
    WorkTimeType = "workTimeTypeId",
    WorkShift = "workShiftId",
}

export interface ReportOptionsState {
    vehicles: VehicleListItemDto[];
    employees: EmployeeListItemDto[];
    employeeGroups: employeeGroupListItemDto[];
    salaryRowTypes: SalaryRowTypeItem[];
    costCenters: ICostCenterItem[];
    customers: ICustomerItem[];
    workTimeTypes: WorkTimeTypeListItem[];
    workTimeFilters: Filters;
    vehicleFilters: Filters;
    workTimeGrouping: string[];
    workTimeGroupByRow: boolean;
    workTimeGroupingModeMultiple: boolean;
    workTimeShowGroupedRows: boolean;
    vehicleGrouping: string[];
    fuelEmissions: FuelEmission[];
}

export const initialState: ReportOptionsState = {
    vehicles: null,
    employees: null,
    employeeGroups: null,
    salaryRowTypes: null,
    costCenters: null,
    customers: null,
    workTimeTypes: null,
    workTimeFilters: {
        timeRange: [
            Base.dayjsToJsonDate(
                Base.dateStartOf(Base.dateAddMonths(new Date(), -1), "month")
            ),
            Base.dayjsToJsonDate(
                Base.dateEndOf(Base.dateAddMonths(new Date(), -1), "month")
            ),
        ],
        selectedVehicles: [],
        selectedCostCenters: [],
        selectedEmployees: [],
        selectedEmployeeGroups: [],
        selectedCustomers: [],
        selectedWorkTimeTypes: [],
    },
    vehicleFilters: {
        timeRange: [
            Base.dayjsToJsonDate(
                Base.dateStartOf(Base.dateAddMonths(new Date(), -1), "month")
            ),
            Base.dayjsToJsonDate(
                Base.dateEndOf(Base.dateAddMonths(new Date(), -1), "month")
            ),
        ],
        selectedVehicles: [],
        selectedCostCenters: [],
        selectedEmployees: [],
        selectedEmployeeGroups: [],
        selectedCustomers: [],
        selectedWorkTimeTypes: [],
    },
    workTimeGrouping: [GroupingValue.Vehicle],
    workTimeGroupByRow: false,
    workTimeGroupingModeMultiple: false,
    workTimeShowGroupedRows: true,
    vehicleGrouping: [GroupingValue.Vehicle],
    fuelEmissions: null,
};

export const reportOptionsSlice = createSlice({
    name: "reportOptions",
    initialState,
    reducers: {
        setVehicles: (state, action: PayloadAction<VehicleListItemDto[]>) => {
            state.vehicles = action.payload;
        },
        setEmployees: (state, action: PayloadAction<EmployeeListItemDto[]>) => {
            state.employees = action.payload;
        },
        setEmployeeGroups: (state, action: PayloadAction<employeeGroupListItemDto[]>) => {
            state.employeeGroups = action.payload;
        },
        setSalaryRowTypes: (
            state,
            action: PayloadAction<SalaryRowTypeItem[]>
        ) => {
            state.salaryRowTypes = action.payload;
        },
        setCostCenters: (state, action: PayloadAction<ICostCenterItem[]>) => {
            state.costCenters = action.payload;
        },
        setCustomers: (state, action: PayloadAction<ICustomerItem[]>) => {
            state.customers = action.payload;
        },
        setWorkTimeTypes: (
            state,
            action: PayloadAction<WorkTimeTypeListItem[]>
        ) => {
            state.workTimeTypes = action.payload;
        },
        setWorkTimeReportTimeRange(state, action: PayloadAction<[string, string]>) {
            state.workTimeFilters.timeRange = action.payload;
        },
        setWorkTimeReportSelectedVehicles(state, action: PayloadAction<string[]>) {
            state.workTimeFilters.selectedVehicles = action.payload;
        },
        setWorkTimeReportSelectedCostCenters(state, action: PayloadAction<string[]>) {
            state.workTimeFilters.selectedCostCenters = action.payload;
        },
        setWorkTimeReportSelectedEmployees(state, action: PayloadAction<string[]>) {
            state.workTimeFilters.selectedEmployees = action.payload;
        },
        setWorkTimeReportSelectedEmployeeGroups(state, action: PayloadAction<string[]>) {
            state.workTimeFilters.selectedEmployeeGroups = action.payload;
        },
        setWorkTimeReportSelectedCustomers(state, action: PayloadAction<string[]>) {
            state.workTimeFilters.selectedCustomers = action.payload;
        },
        setWorkTimeReportSelectedWorkTimeTypes(state, action: PayloadAction<string[]>) {
            state.workTimeFilters.selectedWorkTimeTypes = action.payload;
        },
        clearWorkTimeReportFilters: (state) => {
            state.workTimeFilters = { ...initialState.workTimeFilters };
        },
        setVehicleReportTimeRange(state, action: PayloadAction<[string, string]>) {
            state.vehicleFilters.timeRange = action.payload;
        },
        setVehicleReportSelectedVehicles(state, action: PayloadAction<string[]>) {
            state.vehicleFilters.selectedVehicles = action.payload;
        },
        setVehicleReportSelectedCostCenters(state, action: PayloadAction<string[]>) {
            state.vehicleFilters.selectedCostCenters = action.payload;
        },
        setVehicleReportSelectedEmployees(state, action: PayloadAction<string[]>) {
            state.vehicleFilters.selectedEmployees = action.payload;
        },
        setVehicleReportSelectedEmployeeGroups(state, action: PayloadAction<string[]>) {
            state.vehicleFilters.selectedEmployeeGroups = action.payload;
        },
        setVehicleReportSelectedCustomers(state, action: PayloadAction<string[]>) {
            state.vehicleFilters.selectedCustomers = action.payload;
        },
        setVehicleReportSelectedWorkTimeTypes(state, action: PayloadAction<string[]>) {
            state.vehicleFilters.selectedWorkTimeTypes = action.payload;
        },
        clearVehilceReportFilters: (state) => {
            state.vehicleFilters = { ...initialState.vehicleFilters };
        },
        setWorkTimeReportGrouping: (state, action: PayloadAction<string[]>) => {
            state.workTimeGrouping = action.payload.filter(Boolean);
        },
        setWorkTimeReportGroupByRow: (state, action: PayloadAction<boolean>) => {
            state.workTimeGroupByRow = action.payload;
        },
        setWorkTimeReportGroupingModeMultiple: (state, action: PayloadAction<boolean>) => {
            state.workTimeGroupingModeMultiple = action.payload;
        },
        setWorkTimeReportShowGroupedRows: (state, action: PayloadAction<boolean>) => {
            state.workTimeShowGroupedRows = action.payload;
        },
        setVehicleReportGrouping: (state, action: PayloadAction<string[]>) => {
            state.vehicleGrouping = action.payload.filter(Boolean);
        },
    },
});

export const {
    setVehicles,
    setEmployees,
    setEmployeeGroups,
    setSalaryRowTypes,
    setCostCenters,
    setCustomers,
    setWorkTimeTypes,
    setWorkTimeReportTimeRange,
    setWorkTimeReportSelectedVehicles,
    setWorkTimeReportSelectedCostCenters,
    setWorkTimeReportSelectedEmployees,
    setWorkTimeReportSelectedEmployeeGroups,
    setWorkTimeReportSelectedCustomers,
    setWorkTimeReportSelectedWorkTimeTypes,
    clearWorkTimeReportFilters,
    setVehicleReportTimeRange,
    setVehicleReportSelectedVehicles,
    setVehicleReportSelectedCostCenters,
    setVehicleReportSelectedEmployees,
    setVehicleReportSelectedEmployeeGroups,
    setVehicleReportSelectedCustomers,
    setVehicleReportSelectedWorkTimeTypes,
    clearVehilceReportFilters,
    setWorkTimeReportGrouping,
    setWorkTimeReportGroupByRow,
    setWorkTimeReportGroupingModeMultiple,
    setWorkTimeReportShowGroupedRows,
    setVehicleReportGrouping,
} = reportOptionsSlice.actions;

export const fetchReportVehicles = createAsyncThunk(
    "reportOptions/vehicles",
    async(_, thunkApi) => {
        const vehicles = await getVehicles(true, thunkApi.signal);
        thunkApi.dispatch(setVehicles(vehicles?.data));
    }
);

export const fetchReportEmployees = createAsyncThunk(
    "reportOptions/employees",
    async(_, thunkApi) => {
        const employees = await getEmployees(null, thunkApi.signal);
        thunkApi.dispatch(setEmployees(employees?.data));
    }
);

export const fetchReportEmployeeGroups = createAsyncThunk(
    "reportOptions/employeeGroups",
    async(_, thunkApi) => {
        const employeeGroups = await getEmployeeGroups(true, thunkApi.signal);
        thunkApi.dispatch(setEmployeeGroups(employeeGroups?.data));
    }
);

export const fetchReportSalaryRowTypes = createAsyncThunk(
    "reportOptions/salaryRowTypes",
    async(_, thunkApi) => {
        const salaryRowTypes = await getSalaryRowTypeItems(
            1000,
            1,
            "",
            "name",
            true
        );
        thunkApi.dispatch(
            setSalaryRowTypes(
                salaryRowTypes?.items.sort((a, b) =>
                    Base.stringCompare(a.code, b.code)
                )
            )
        );
    }
);

export const fetchReportCostCenters = createAsyncThunk(
    "reportOptions/costCenters",
    async(_, thunkApi) => {
        // NOTE: Using getCostCenterItems for getting available costCenter names in report is not good way as it uses paging and can limit results
        // if results are limited report cannot find the Cost center name for costCenterId and shows "No customer"
        const costCenters = await getCostCenterItems(
            10000, // Lifted from 1000 to 10 thousand, but still limit can be reached
            1,
            "",
            "name",
            true,
            true
        );
        thunkApi.dispatch(setCostCenters(costCenters?.items));
    }
);

export const fetchReportCustomers = createAsyncThunk(
    "reportOptions/customers",
    async(_, thunkApi) => {
        // NOTE: Using getCustomerItems for getting available customer names in report is not good way as it uses paging and can limit results
        // if results are limited report cannot find the Customer name for customerId and shows "No customer"
        const customers = await getCustomerItems(
            10000, // Lifted from 1000 to 10 thousand, but still limit can be reached
            1,
            "",
            "name",
            true,
            true
        );

        // Get rid of duplicated items:
        const distinctCustomers = customers?.items.filter((item, index, self) => index === self.findIndex((t) => t.name === item.name));

        thunkApi.dispatch(setCustomers(distinctCustomers));
    }
);

export const fetchReportWorkTimeTypes = createAsyncThunk(
    "reportOptions/workTimeTypes",
    async(_, thunkApi) => {
        const workTimeTypes = await getAllWorkTimeTypes(thunkApi.signal);
        thunkApi.dispatch(setWorkTimeTypes(workTimeTypes));
    }
);

export const reportOptionsReducer = reportOptionsSlice.reducer;
